/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { Form, Modal } from 'antd';
import { useState } from 'react';

import { useMessage } from '@/hooks/message';
import { useNewSubtenantMutation } from '@/hooks/mutation/useNewSubtenantMutation';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { SubtenantForm, SubtenantFormFields } from '@/pages/Subtenants/SubtenantForm';

type NewSubtenantModalProps = {
  newSubtenantOpen: boolean;
  onCancelModal: () => void;
};

export function NewSubtenantModal({ newSubtenantOpen, onCancelModal }: NewSubtenantModalProps) {
  const { t } = useMimicTranslation('subtenants');
  const [form] = Form.useForm<SubtenantFormFields>();
  const [creationIsPending, setCreationIsPending] = useState<boolean>(false);
  const [message] = useMessage();

  const { mutate: createNewSubtenant } = useNewSubtenantMutation({
    onError: (_, subtenant) => {
      message.error(t('newSubtenantCreatedError', subtenant));
      setCreationIsPending(false);
    },
    onSuccess: (_, deletedSubtenant) => {
      message.success(t('newSubtenantCreated', deletedSubtenant));
      closeDeleteDialog();
      setCreationIsPending(false);
    },
    onMutate: () => {
      setCreationIsPending(true);
    },
  });

  const closeDeleteDialog = () => {
    form.resetFields();
    onCancelModal();
  };

  const onFormFinish = async () => {
    try {
      await form.validateFields();
      const name = form.getFieldValue('name');
      const description = form.getFieldValue('description');
      const subtenantId = form.getFieldValue('subtenantId');
      createNewSubtenant({ name, description, subtenantId });
      onCancelModal();
    } catch (error) {
      // no op
    }
  };

  return (
    newSubtenantOpen && (
      <Modal
        title={t('addSubtenant.modal')}
        open={newSubtenantOpen}
        onCancel={closeDeleteDialog}
        onOk={onFormFinish}
        cancelText={t('subtenantForm.cancel')}
        cancelButtonProps={{ disabled: creationIsPending }}
        okText={t('subtenantForm.ok')}
        okButtonProps={{ loading: creationIsPending }}
      >
        <SubtenantForm loading={creationIsPending} form={form} />
      </Modal>
    )
  );
}
